/**
 * Einheitliches Datumsformat im CRM: deutsch, „26.10.2026" — unabhängig von
 * UI-Sprache und Browser-Locale (Vorgabe 10/2026: „überall das deutsche
 * Format"). Texte mit Monats-/Wochentagsnamen („Montag, 5. Oktober") bleiben
 * Sache der jeweiligen Ansicht; alles Numerische läuft hier durch.
 *
 * Reine Datumswerte („2026-10-26") werden NICHT über `new Date()` geparst —
 * das wäre UTC-Mitternacht und könnte je nach Zeitzone auf den Vortag kippen
 * (s. localDate.ts).
 */

const pad = (n: number) => String(n).padStart(2, '0');

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

type DateLike = Date | string | number | null | undefined;

interface Parts { y: number; m: number; d: number; hh: number; mm: number; hasTime: boolean }

function toParts(value: DateLike): Parts | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const s = value.trim();
    const only = s.match(DATE_ONLY);
    if (only) return { y: +only[1], m: +only[2], d: +only[3], hh: 0, mm: 0, hasTime: false };
    // MySQL-Datetime „2026-10-26 14:30:00" ohne Offset → lokale Zeit (wie Safari-sicher).
    const sql = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
    if (sql) return { y: +sql[1], m: +sql[2], d: +sql[3], hh: +sql[4], mm: +sql[5], hasTime: true };
  }
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return {
    y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate(),
    hh: dt.getHours(), mm: dt.getMinutes(), hasTime: true,
  };
}

/** „26.10.2026" — leer/ungültig → `fallback` (Default „—"). */
export function formatDate(value: DateLike, fallback = '—'): string {
  const p = toParts(value);
  return p ? `${pad(p.d)}.${pad(p.m)}.${p.y}` : fallback;
}

/** „26.10.2026 14:30" — reines Datum ohne Uhrzeit → nur „26.10.2026". */
export function formatDateTime(value: DateLike, fallback = '—'): string {
  const p = toParts(value);
  if (!p) return fallback;
  const date = `${pad(p.d)}.${pad(p.m)}.${p.y}`;
  return p.hasTime ? `${date} ${pad(p.hh)}:${pad(p.mm)}` : date;
}

/** „26.10." — für kompakte Listen (Posteingang, Chat, Header). */
export function formatDayMonth(value: DateLike, fallback = '—'): string {
  const p = toParts(value);
  return p ? `${pad(p.d)}.${pad(p.m)}.` : fallback;
}

/** „14:30" (24 h). */
export function formatTime(value: DateLike, fallback = '—'): string {
  const p = toParts(value);
  return p && p.hasTime ? `${pad(p.hh)}:${pad(p.mm)}` : fallback;
}

/**
 * Eingabe „TT.MM.JJJJ" → „YYYY-MM-DD"; null bei ungültiger Eingabe, "" bei
 * leerer. Akzeptiert auch „1.2.2026", „01.02.26", „010226", „01022026" und
 * ISO „2026-02-01". Zweistellige Jahre → 20xx.
 */
export function parseGermanDate(input: string): string | null {
  const s = input.trim();
  if (s === '') return '';
  let d: number; let m: number; let y: number;
  const iso = s.match(DATE_ONLY);
  const sep = s.match(/^(\d{1,2})[.\-/ ](\d{1,2})[.\-/ ](\d{2}|\d{4})$/);
  const compact = s.match(/^(\d{2})(\d{2})(\d{2}|\d{4})$/);
  if (iso) { y = +iso[1]; m = +iso[2]; d = +iso[3]; }
  else if (sep) { d = +sep[1]; m = +sep[2]; y = +sep[3]; }
  else if (compact) { d = +compact[1]; m = +compact[2]; y = +compact[3]; }
  else return null;
  if (y < 100) y += 2000;
  const check = new Date(y, m - 1, d);
  if (check.getFullYear() !== y || check.getMonth() !== m - 1 || check.getDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}
